import type { Paragraph, Table } from "docx";
import {
  SAFETY_ROUND_STATUS_LABELS,
  SAFETY_ROUND_TYPE_LABELS,
  type SafetyRoundStatus,
  type SafetyRoundType,
} from "@/lib/basProcess/constants";
import { downloadWordDocument } from "@/lib/documentGenerator/exportHistory";
import { createStampForCurrentUser } from "@/lib/documentGenerator/watermark";
import {
  body,
  buildDocument,
  factTable,
  fmtDate,
  h1,
  signatureBlock,
  table,
} from "@/lib/documentGenerator/docTheme";
import { documentFooter } from "@/lib/documentGenerator/documentFooter";

export interface SafetyRoundExportItem {
  section: string;
  control_point: string;
  legal_reference: string | null;
  status: string;
  responsible: string | null;
  due_date: string | null;
  comment: string | null;
}

export const generateSafetyRoundWordDocument = async (round: {
  id?: string;
  name: string;
  round_type: string;
  round_date: string | null;
  area: string | null;
  participants: string | null;
  status: string;
  notes: string | null;
  items: SafetyRoundExportItem[];
}, projectName?: string) => {
  const documentName = round.name || "Skyddsrond";
  const statusLabel = round.status === "signed" ? "Genomförd och signerad" : "Utkast";

  const stamp = await createStampForCurrentUser({
    docNumber: "27",
    documentName,
    projectName,
  });

  const children: (Paragraph | Table)[] = [
    h1("1. Grunduppgifter"),
    factTable([
      ["Typ av rond", SAFETY_ROUND_TYPE_LABELS[(round.round_type as SafetyRoundType) ?? "ordinarie"]],
      ["Datum", round.round_date ? fmtDate(round.round_date) : ""],
      ["Område / del av arbetsplatsen", round.area],
      ["Deltagare", round.participants],
      ["Status", statusLabel],
    ]),
  ];

  const sections = Array.from(new Set(round.items.map((i) => i.section)));
  sections.forEach((section, index) => {
    children.push(
      h1(`${index + 2}. ${section}`),
      table(
        ["Kontrollpunkt", "Status", "Ansvarig", "Klart senast", "Kommentar"],
        round.items
          .filter((i) => i.section === section)
          .map((i) => [
            i.legal_reference ? `${i.control_point}\n(${i.legal_reference})` : i.control_point,
            SAFETY_ROUND_STATUS_LABELS[(i.status as SafetyRoundStatus) ?? "unanswered"] || i.status,
            i.responsible,
            i.due_date ? fmtDate(i.due_date) : "",
            i.comment,
          ]),
        { columnWidths: [3200, 1300, 1500, 1200, 2160] }
      )
    );
  });

  if (round.notes) {
    children.push(h1("Sammanfattande noteringar"), body(round.notes));
  }
  const footer = await documentFooter({ docNumber: "27", documentType: "skyddsrond" });


  const doc = buildDocument({
    meta: {
      version: footer.version,
      review: footer.review,
      docNumber: "27",
      documentName,
      projectName,
      status: statusLabel,
      date: round.round_date,
    },
    stamp,
    children: [...children, ...footer.children],
  });

  const safe = (name?: string) => (name || "projekt").replace(/[^\w-]+/g, "_");
  await downloadWordDocument(doc, `Dok27_Skyddsrondsprotokoll_${safe(projectName)}.docx`, {
    documentType: "skyddsrond",
    docNumber: "27",
    documentId: (round as { id?: string }).id ?? null,
    documentName,
    snapshot: round,
    stamp,
  });
};
